import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Heading,
  Text,
  VStack,
  Spinner,
  Icon,
  Image,
  AspectRatio,
  Badge,
  Flex,
  Button,
} from '@chakra-ui/react';
import { COLORS } from '../utils/constants';
import { getBackendUrl } from '../config/environments';
import { FaPlay, FaImage, FaVideo } from 'react-icons/fa';

// Gallery Item Component with hover effects
const GalleryItem = ({ item }) => {
  const isVideo = item.media_type === 'video';
  
  return (
    <Box
      sx={{
        '--border-top-width': '0.5px',
        '--border-right-width': '0.5px',
        '--border-bottom-width': '0.5px',
        '--border-left-width': '0.5px',
        '--border-style': 'dashed',
        '--border-color': 'rgb(7, 74, 0)',
        'borderWidth': 'var(--border-top-width) var(--border-right-width) var(--border-bottom-width) var(--border-left-width)',
        'borderStyle': 'var(--border-style)',
        'borderColor': 'var(--border-color)'
      }}
      borderRadius="10px"
      overflow="hidden"
      bg="linear-gradient(235.162deg, rgba(0, 0, 0, 0.85) 0%, rgba(2, 39, 8, 0.3) 50%, rgba(0, 0, 0, 0.85) 100%)"
      transition="all 0.3s ease-in-out"
      boxShadow="rgb(32, 230, 13) 0px 0px 15px -8px"
      _hover={{
        transform: 'translateY(-8px)',
        boxShadow: '0 15px 35px rgba(32, 230, 13, 0.4)',
        borderColor: 'rgba(32, 230, 13, 0.6)'
      }}
      className="glow-border"
      position="relative"
      role="group"
    >
      <AspectRatio ratio={1}>
        <Box position="relative" w="100%" h="100%">
          {isVideo ? (
            <>
              <video
                src={item.url}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                muted
                playsInline
                preload="metadata"
              />
              <Box
                position="absolute"
                inset={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="blackAlpha.400"
                opacity={0}
                transition="opacity 0.3s ease"
                _groupHover={{ opacity: 1 }}
                cursor="pointer"
                onClick={() => {
                  const video = document.querySelector(`video[src="${item.url}"]`);
                  if (video) {
                    if (video.paused) {
                      video.play();
                    } else {
                      video.pause();
                    }
                  }
                }}
              >
                <Icon as={FaPlay} color="white" boxSize={10} />
              </Box>
            </>
          ) : (
            <Image
              src={item.url}
              alt={item.prompt}
              objectFit="cover"
              w="100%"
              h="100%"
              transition="transform 0.3s ease"
              _groupHover={{ transform: 'scale(1.05)' }}
              fallback={
                <Flex align="center" justify="center" h="100%" bg="gray.700">
                  <Icon as={FaImage} color="gray.400" boxSize={10} />
                </Flex>
              }
            />
          )}
          
          {/* Media type badge */}
          <Badge
            position="absolute"
            top={3}
            right={3}
            color={"green"}
            background={"black"}
            borderRadius={"5px"}
            colorScheme={isVideo ? "purple" : "blue"}
            size="sm"
            leftIcon={<Icon as={isVideo ? FaVideo : FaImage} />}
          >
            {isVideo ? 'Video' : ''}
          </Badge>
        </Box>
      </AspectRatio>
      
      {/* Content overlay */}
      <Box p={4}>
        <Text
          color="white"
          fontSize="sm"
          fontWeight="medium"
          mb={2}
          noOfLines={2}
          lineHeight="1.3"
        >
          {item.prompt}
        </Text>
        
        {/* <Flex justify="space-between" align="center" fontSize="xs" color="gray.400">
          <Flex align="center" gap={1}>
            <Icon as={FaUser} />
            <Text>{item.creator?.displayName || 'Anonymous'}</Text>
          </Flex>
          {item.generationTime && (
            <Text>{item.generationTime}s</Text>
          )}
        </Flex> */}
        
        {item.style && (
          <Text fontSize="xs" color={COLORS.primary} mt={1}>
            {item.style}
          </Text>
        )}
      </Box>
    </Box>
  );
};

// Loading skeleton component
const GallerySkeleton = () => (
  <Grid
    templateColumns={{
      base: 'repeat(1, 1fr)',
      sm: 'repeat(2, 1fr)',
      md: 'repeat(3, 1fr)',
      lg: 'repeat(4, 1fr)',
    }}
    gap={6}
  >
    {Array.from({ length: 8 }).map((_, i) => (
      <Box
        key={i}
        borderRadius="20px"
        bg="rgba(0, 0, 0, 0.3)"
        border="1px dashed rgba(7, 74, 0, 0.3)"
        overflow="hidden"
      >
        <AspectRatio ratio={1}>
          <Box bg="gray.700" />
        </AspectRatio>
        <Box p={4}>
          <Box h={4} bg="gray.600" borderRadius="md" mb={2} />
          <Box h={3} bg="gray.700" borderRadius="md" w="60%" />
        </Box>
      </Box>
    ))}
  </Grid>
);

function Gallery() {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const sentinel = useRef(null);
  const isInitialized = useRef(false);

  // Fetch gallery data - stable function that doesn't recreate
  const fetchGalleryData = async (resetItems = false, customCursor = null) => {
    if (loading) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      const currentCursor = customCursor !== null ? customCursor : cursor;
      if (currentCursor && !resetItems) {
        params.append('cursor', currentCursor);
      }
      params.append('limit', '24');
      
      console.log('🖼️ Fetching gallery data...');
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/gallery?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch gallery data');
      }
      
      console.log('✅ Gallery data received:', data.data.length, 'items');
      
      if (resetItems) {
        setItems(data.data);
      } else {
        setItems(prev => [...prev, ...data.data]);
      }
      
      setCursor(data.nextCursor);
      setHasMore(!!data.nextCursor);
      
    } catch (err) {
      console.error('❌ Gallery fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  // Initial load - only run once
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      fetchGalleryData(true);
    }
  }, []);

  // Infinite scroll setup
  useEffect(() => {
    if (!sentinel.current || !hasMore || loading) return;
    
    const currentSentinel = sentinel.current; // Store ref for cleanup
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loading && hasMore) {
          console.log('📜 Loading more items...');
          fetchGalleryData(false, cursor);
        }
      },
      { rootMargin: '400px' }
    );
    
    observer.observe(currentSentinel);
    
    return () => {
      observer.disconnect();
    };
  }, [hasMore, loading, cursor]); // Only depend on what we actually need

  if (initialLoad && loading) {
    return (
      <Container maxW="7xl" py={8}>
        <VStack spacing={6} align="stretch">
          <Heading as="h1" size="xl" color="white" mb={4}>
            Community Gallery
          </Heading>
          <Text color="gray.300" fontSize="lg" mb={8}>
            Discover amazing AI-generated images and videos from our community
          </Text>
          <GallerySkeleton />
        </VStack>
      </Container>
    );
  }

  if (error && items.length === 0) {
    return (
      <Container maxW="7xl" py={8}>
        <VStack spacing={6} align="center" py={20}>
          <Heading as="h1" size="xl" color="white" mb={4}>
            Community Gallery
          </Heading>
          <Box
            p={8}
            borderRadius="xl"
            bg="rgba(255, 0, 0, 0.1)"
            border="1px dashed rgba(255, 0, 0, 0.3)"
            textAlign="center"
          >
            <Text color="red.300" fontSize="lg" mb={4}>
              Failed to load gallery
            </Text>
            <Text color="gray.400" mb={6}>
              {error}
            </Text>
            <Button
              className="primary-button"
              onClick={() => fetchGalleryData(true)}
            >
              Try Again
            </Button>
          </Box>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="7xl" py={8}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading as="h1" size="xl" color="white" mb={4}>
            Community Gallery
          </Heading>
          <Text color="gray.300" fontSize="lg" mb={2}>
            Discover amazing AI-generated images and videos from our community
          </Text>
          {/* {items.length > 0 && (
            <Text color="gray.500" fontSize="sm">
              {items.length} creations and counting...
            </Text>
          )} */}
        </Box>

        {items.length === 0 && !loading ? (
          <Box
            py={20}
            textAlign="center"
            bg="rgba(0, 0, 0, 0.2)"
            borderRadius="xl"
            border="1px dashed"
            borderColor={COLORS.primary}
          >
            <VStack spacing={6}>
              <Icon as={FaImage} color={COLORS.primary} boxSize={16} />
              <Heading as="h2" size="xl" color="white">
                No creations yet
              </Heading>
              <Text fontSize="lg" color="gray.300" maxW="xl" mx="auto">
                Be the first to share your AI creations with the community!
              </Text>
            </VStack>
          </Box>
        ) : (
          <>
            <Grid
              templateColumns={{
                base: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              }}
              gap={6}
              autoRows="1fr"
            >
              {items.map((item) => (
                <GalleryItem key={item.id} item={item} />
              ))}
            </Grid>

            {/* Loading indicator for infinite scroll */}
            {loading && (
              <Flex justify="center" py={8}>
                <VStack spacing={4}>
                  <Spinner size="lg" color={COLORS.primary} thickness="3px" />
                  <Text color="gray.400">Loading more creations...</Text>
                </VStack>
              </Flex>
            )}

            {/* Sentinel for infinite scroll */}
            <div ref={sentinel} />

            {/* End message */}
            {!hasMore && !loading && items.length > 0 && (
              <Text
                textAlign="center"
                color="gray.500"
                fontSize="sm"
                py={6}
              >
                You've reached the end!
              </Text>
            )}
          </>
        )}
      </VStack>
    </Container>
  );
}

export default Gallery; 